<script setup lang="ts">
  import { useRestaurant } from '@/composition/use-restaurant';
  import { useUi } from '@/composition/use-ui';
  import { IPayment } from '@/interfaces/IPayment';
  import { IOrder } from '@/interfaces/IOrder';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { inject, ref, toRef, watch } from 'vue';
  import PaymentsService from '@/api/services/payments.service';
  import { PaymentEntityType } from '@/enums/PaymentEntityType';
  import { PaymentStatus, PaymentType } from '@/enums/PaymentEnums';
  import { PAYMENT_METHODS_MAP } from '@/const/payment.consts';
  import { usePaymentRefund } from '@/composition/use-payment-refund';

  const props = defineProps<{
    order: IOrder;
    showingPayments: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:showingPayments', value: boolean): void;
  }>();

  const restaurantStore = useRestaurantStore();
  const restaurantHelper = useRestaurant();
  const ui = useUi();
  const { isRefunding, refundPayment } = usePaymentRefund(
    toRef(props, 'order')
  );

  const payments = ref<IPayment[]>(null);

  // POS V2 preview modals re-host this tree read-only: hide the refund action.
  const posV2ViewMode = inject<'interactive' | 'preview'>(
    'posV2ViewMode',
    'interactive'
  );
  const isPreview = posV2ViewMode === 'preview';

  async function loadPayments() {
    if (props.order.totalPaid <= 0) return;

    try {
      payments.value = null;
      payments.value = (
        await PaymentsService.v2FindByReference(
          PaymentEntityType.Order,
          props.order.id,
          {
            itemsPerPage: 1000,
            mustSort: true,
            sortBy: ['createdAt'],
            sortDesc: [true],
          }
        )
      ).items;
      payments.value = payments.value.filter(
        (m) => m.status === PaymentStatus.Paid && m.type === PaymentType.Order
      );
    } catch (e) {
      ui.serverError(e, 'Ocurrió un error al obtener los movimientos de caja');
    }
  }

  async function handleRefund(payment: IPayment) {
    const done = await refundPayment({
      payment,
      amount: payment.total,
      shared: false,
    });
    if (done) await loadPayments();
  }

  watch(
    () => props.showingPayments,
    (newVal) => {
      if (newVal) {
        loadPayments();
      }
    }
  );
</script>

<template>
  <div>
    <div class="summary-line payments-line">
      <div class="summary-title">
        <span>Pagos</span>
        <v-btn
          v-if="order.totalPaid > 0"
          icon
          x-small
          @click="emit('update:showingPayments', !showingPayments)"
        >
          <v-icon small>
            {{ showingPayments ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </v-btn>
      </div>
      <div class="summary-value">
        {{ restaurantStore.currency.symbol }}{{ displayPrice(order.totalPaid) }}
      </div>
    </div>

    <template v-if="showingPayments">
      <div
        v-if="!payments"
        class="tw-p-2 tw-text-center tw-text-xs tw-text-gray-500"
      >
        Cargando...
      </div>
      <div
        v-else-if="!payments.length"
        class="tw-p-2 tw-text-center tw-text-xs tw-text-gray-500"
      >
        No se encontraron pagos
      </div>
      <div v-else class="movements">
        <div
          v-for="(movement, index) in payments"
          :key="movement.id"
          class="movement"
          :class="{ reimbursed: movement.status !== PaymentStatus.Paid }"
        >
          <span class="index">{{ payments.length - index }}.</span>
          <span class="description">
            <span class="movement-amount">
              {{ restaurantStore.currency.symbol
              }}{{ displayPrice(movement.total) }}
              <span class="movement-method">
                ({{
                  PAYMENT_METHODS_MAP.get(movement.paymentMethod)?.title ||
                  'Otro'
                }})
              </span>
            </span>
            <timeago class="timeago" :datetime="movement.createdAt"></timeago>
          </span>
          <v-btn
            v-if="!isPreview"
            x-small
            icon
            color="error"
            class="refund-btn"
            :loading="isRefunding"
            @click="handleRefund(movement)"
            title="Reembolsar pago"
          >
            <v-icon small>mdi-cash-refund</v-icon>
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .summary-line {
    @apply tw-flex tw-justify-between tw-text-gray-600 tw-text-sm;
    font-family: 'Courier New', monospace;

    &.payments-line {
      @apply tw-border-t-2 tw-border-dashed tw-border-gray-300 tw-pt-2 tw-mt-2;

      .summary-title {
        @apply tw-flex tw-items-center tw-font-medium tw-uppercase tw-text-xs tw-tracking-wider;
      }

      .summary-value {
        @apply tw-font-mono tw-font-semibold;
      }
    }
  }

  .movements {
    @apply tw-space-y-1 tw-pl-4 tw-border-l-2 tw-border-dashed tw-border-gray-300 tw-mt-2;

    .movement {
      @apply tw-flex tw-items-center tw-space-x-2 tw-text-sm;
      font-family: 'Courier New', monospace;

      .index {
        @apply tw-text-gray-500 tw-text-xs tw-font-mono;
      }

      .description {
        @apply tw-flex-1 tw-flex tw-flex-col;

        .movement-amount {
          @apply tw-font-semibold tw-font-mono;

          .movement-method {
            @apply tw-text-xs tw-text-gray-600 tw-font-normal tw-uppercase tw-tracking-wider;
          }
        }

        .timeago {
          @apply tw-text-xs tw-text-gray-500 tw-font-mono;
        }
      }

      .refund-btn {
        // Remove hover-only visibility
      }

      &.reimbursed {
        @apply tw-line-through tw-text-gray-500;

        .refund-btn {
          display: none;
        }
      }
    }
  }
</style>
